/**
 * Componente de Validação Reutilizável
 * Fornece funcionalidades padronizadas para validação de formulários
 */

/**
 * Configuração padrão para validação
 */
const defaultValidationConfig = {
    errorClass: 'is-invalid',
    successClass: 'is-valid',
    errorMessageClass: 'invalid-feedback',
    showSuccessIndicator: true,
    validateOnBlur: true,
    validateOnInput: false
};

/**
 * Regras de validação comuns
 */
const validationRules = {
    required: (value) => value && value.trim().length > 0,
    email: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
    cpf: (value) => validateCPF(value),
    cnpj: (value) => validateCNPJ(value),
    phone: (value) => /^\(?\d{2}\)?[\s-]?\d{4,5}[\s-]?\d{4}$/.test(value),
    cep: (value) => /^\d{5}-?\d{3}$/.test(value),
    minLength: (min) => (value) => value && value.length >= min,
    maxLength: (max) => (value) => value && value.length <= max,
    numeric: (value) => /^\d+$/.test(value),
    decimal: (value) => /^\d+(\.\d{1,2})?$/.test(value),
    password: (value) => value && value.length >= 8 && /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)
};

/**
 * Mensagens de erro padrão
 */
const defaultMessages = {
    required: 'Este campo é obrigatório',
    email: 'Digite um email válido',
    cpf: 'Digite um CPF válido',
    cnpj: 'Digite um CNPJ válido',
    phone: 'Digite um telefone válido',
    cep: 'Digite um CEP válido',
    minLength: (min) => `Mínimo de ${min} caracteres`,
    maxLength: (max) => `Máximo de ${max} caracteres`,
    numeric: 'Digite apenas números',
    decimal: 'Digite um valor decimal válido',
    password: 'Senha deve ter pelo menos 8 caracteres, incluindo maiúscula, minúscula e número'
};

/**
 * Classe principal para validação de formulários
 */
export class FormValidator {
    constructor(form, config = {}) {
        this.form = $(form);
        this.config = { ...defaultValidationConfig, ...config };
        this.fields = new Map();
        this.isValid = true;
        
        this.init();
    }
    
    init() {
        this.setupEventListeners();
    }
    
    setupEventListeners() {
        if (this.config.validateOnBlur) {
            this.form.on('blur', 'input, select, textarea', (e) => {
                this.validateField($(e.target));
            });
        }
        
        if (this.config.validateOnInput) {
            this.form.on('input', 'input, textarea', (e) => {
                this.validateField($(e.target));
            });
        }
        
        this.form.on('submit', (e) => {
            if (!this.validateAll()) {
                e.preventDefault();
                return false;
            }
        });
    }
    
    addField(selector, rules, customMessages = {}) {
        const field = {
            element: $(selector),
            rules: Array.isArray(rules) ? rules : [rules],
            messages: { ...defaultMessages, ...customMessages }
        };
        
        this.fields.set(selector, field);
        return this;
    }
    
    validateField(element) {
        const selector = this.getFieldSelector(element);
        const field = this.fields.get(selector);
        
        if (!field) return true;
        
        const value = element.val();
        let isValid = true;
        let errorMessage = '';
        
        for (const rule of field.rules) {
            const result = this.applyRule(rule, value);
            if (!result.valid) {
                isValid = false;
                errorMessage = result.message || field.messages[rule.name] || 'Campo inválido';
                break;
            }
        }
        
        this.updateFieldUI(element, isValid, errorMessage);
        return isValid;
    }
    
    applyRule(rule, value) {
        if (typeof rule === 'string') {
            const ruleFn = validationRules[rule];
            return {
                valid: ruleFn ? ruleFn(value) : true,
                message: defaultMessages[rule]
            };
        }
        
        if (typeof rule === 'object') {
            const { name, params, message } = rule;
            const ruleFn = validationRules[name];
            
            if (!ruleFn) return { valid: true };
            
            const validator = params ? ruleFn(...params) : ruleFn;
            return {
                valid: typeof validator === 'function' ? validator(value) : validator,
                message: message || (typeof defaultMessages[name] === 'function' ? 
                    defaultMessages[name](...(params || [])) : defaultMessages[name])
            };
        }
        
        if (typeof rule === 'function') {
            return { valid: rule(value) };
        }
        
        return { valid: true };
    }
    
    updateFieldUI(element, isValid, errorMessage) {
        element.removeClass(`${this.config.errorClass} ${this.config.successClass}`);
        
        if (isValid) {
            if (this.config.showSuccessIndicator) {
                element.addClass(this.config.successClass);
            }
            this.removeErrorMessage(element);
        } else {
            element.addClass(this.config.errorClass);
            this.showErrorMessage(element, errorMessage);
        }
    }
    
    showErrorMessage(element, message) {
        this.removeErrorMessage(element);
        
        const errorDiv = $(`<div class="${this.config.errorMessageClass}">${message}</div>`);
        element.after(errorDiv);
    }
    
    removeErrorMessage(element) {
        element.siblings(`.${this.config.errorMessageClass}`).remove();
    }
    
    validateAll() {
        let allValid = true;
        
        this.fields.forEach((field, selector) => {
            const isValid = this.validateField(field.element);
            if (!isValid) {
                allValid = false;
            }
        });
        
        this.isValid = allValid;
        return allValid;
    }
    
    getFieldSelector(element) {
        // Tenta encontrar o seletor baseado no elemento
        for (const [selector, field] of this.fields) {
            if (field.element.is(element)) {
                return selector;
            }
        }
        return null;
    }
    
    reset() {
        this.fields.forEach((field) => {
            field.element.removeClass(`${this.config.errorClass} ${this.config.successClass}`);
            this.removeErrorMessage(field.element);
        });
        this.isValid = true;
    }
    
    getErrors() {
        const errors = [];
        this.fields.forEach((field, selector) => {
            if (!this.validateField(field.element)) {
                errors.push({
                    field: selector,
                    element: field.element
                });
            }
        });
        return errors;
    }
}

/**
 * Funções utilitárias para validação
 */
function validateCPF(cpf) {
    cpf = cpf.replace(/[^\d]/g, '');
    
    if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) {
        return false;
    }
    
    let sum = 0;
    for (let i = 0; i < 9; i++) {
        sum += parseInt(cpf.charAt(i)) * (10 - i);
    }
    
    let remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cpf.charAt(9))) return false;
    
    sum = 0;
    for (let i = 0; i < 10; i++) {
        sum += parseInt(cpf.charAt(i)) * (11 - i);
    }
    
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    
    return remainder === parseInt(cpf.charAt(10));
}

function validateCNPJ(cnpj) {
    cnpj = cnpj.replace(/[^\d]/g, '');
    
    if (cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj)) {
        return false;
    }
    
    const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const weights2 = [6, 7, 8, 9, 2, 3, 4, 5, 6, 7, 8, 9];
    
    let sum = 0;
    for (let i = 0; i < 12; i++) {
        sum += parseInt(cnpj.charAt(i)) * weights1[i];
    }
    
    let remainder = sum % 11;
    const digit1 = remainder < 2 ? 0 : 11 - remainder;
    
    if (digit1 !== parseInt(cnpj.charAt(12))) return false;
    
    sum = 0;
    for (let i = 0; i < 13; i++) {
        sum += parseInt(cnpj.charAt(i)) * weights2[i];
    }
    
    remainder = sum % 11;
    const digit2 = remainder < 2 ? 0 : 11 - remainder;
    
    return digit2 === parseInt(cnpj.charAt(13));
}

/**
 * Factory functions para uso rápido
 */
export function createValidator(form, config) {
    return new FormValidator(form, config);
}

export function setupQuickValidation(form, fields) {
    const validator = new FormValidator(form);
    
    Object.entries(fields).forEach(([selector, rules]) => {
        validator.addField(selector, rules);
    });
    
    return validator;
}

/**
 * Exportar regras e mensagens para uso externo
 */
export { validationRules, defaultMessages };
